import React, {useState, ChangeEvent} from 'react';
import {
  chakra,
  Box, BoxProps,
  VStack,
  Table, Tbody, Tr, Th, Td,
  Input, Select,
  Button, ButtonProps,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody,
  useDisclosure
} from '@chakra-ui/react';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import DeleteButton from './DeleteButton';
import AddButton from './AddButton';
import Handle from './Handle';
import {createDividual, duplicateDividual} from '../models';
import {CHART_COLORS} from '../theme';

type Props = BoxProps & {
  estate: Estate;
  setEstate(estate: Estate): void;
  estates: Estate[];
};

export default function RatioInputs({
  estate, setEstate, estates, ...boxProps
}: Props) {
  const ratios = estate.dividuals.map(dividual => dividual.ratio);
  const sum = ratios.reduce((s, ratio) => s + ratio, 0 as number);
  const percentages = ratios.map(ratio => ratio * 100 / sum); // FIXME: 最後の項目は引き算で求める
  const onNameChange = (index: number, event: ChangeEvent<HTMLInputElement>) => setEstate({
    ...estate,
    dividuals: [
      ...estate.dividuals.slice(0, index),
      {
        ...estate.dividuals[index],
        name: event.target.value
      },
      ...estate.dividuals.slice(index + 1)
    ]
  });
  const onRatioChange = (index: number, event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => setEstate({
    ...estate,
    dividuals: [
      ...estate.dividuals.slice(0, index),
      {
        ...estate.dividuals[index],
        ratio: Math.min(Math.max(Number.parseInt(event.target.value || "1"), 1), 10) as Ratio
      },
      ...estate.dividuals.slice(index + 1)
    ]
  });
  const onDelete = (index: number) => setEstate({
    ...estate,
    dividuals: [
      ...estate.dividuals.slice(0, index),
      ...estate.dividuals.slice(index + 1)
    ]
  });
  const addDividualInput = () => setEstate({
    ...estate,
    dividuals: [...estate.dividuals, createDividual()]
  });

  return (
    <Box {...boxProps}>
      <DragDropContext onDragEnd={result => {
        function reorder(estate: Estate, startIndex: number, endIndex: number) {
          const dividuals = Array.from(estate.dividuals);
          const [removed] = dividuals.splice(startIndex, 1);
          dividuals.splice(endIndex, 0, removed);
          return { ...estate, dividuals };
        }
        if (!result.destination) {
          return;
        }
        setEstate(reorder(estate, result.source.index, result.destination.index));
      }}>
        <Droppable droppableId={`estate:${estate.id}-dividuals`}>
          {(provided) => (
            <Table>
              <Tbody
                {...provided.droppableProps}
                ref={provided.innerRef}
                lineHeight="none"
              >
                <Tr display={['none', 'none', "flex"]} width="full">
                  <Th display="block" flexShrink="0" w="4" p="0">{/* 番号 */}</Th>
                  <Th display="block" flexShrink="0" w="7" p="0" ml="4">{/* 削除 */}</Th>
                  <Th display="flex" flexGrow="0.92" p="0" fontWeight="normal" textAlign="center" ml="4" justifyContent="center" alignItems="center" gap="4">
                    分人
                    <DividualExamples h="1rem" my="auto" />
                  </Th>
                  <Th display="block" flexShrink="0" w="4.5rem" p="0" fontWeight="normal" textAlign="center" ml="2">比率</Th>
                  <Th display="block" flexShrink="0" w="2.125rem" p="0" ml="2.5">{/* パーセンテージ */}</Th>
                  <Th display="block" flexShrink="0" w="6" p="0" ml="6">{/* ハンドル */}</Th>
                </Tr>
                {estate.dividuals.map(({ id, name, ratio }, index) => (
                  <Draggable key={id} draggableId={`estate:${estate.id}-dividual:${id}`} index={index}>
                    {(provided) => (
                      <Tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        display={['grid', 'grid', 'flex']}
                        alignItems="center"
                        w="full"
                        h={['104px', '104px', 'auto']}
                        mt={index === 0 ? 1 : 3}
                        position="relative"
                        gridTemplateAreas={`
                            "num  .    .   handle"
                            "del ind ratio perc"
                          `}
                      >
                        <Td display="block" flexShrink="0" w={['2ch', '2ch', 4]} p="0" color={CHART_COLORS[index].text} position={['absolute', 'absolute', 'relative']} top="0" left="0" fontSize={['1.75rem', '1.75rem', "3xl"]} textAlign="center" gridArea="num" transform={index === 9 ? ['', '', 'translateX(-100%)'] : ''} whiteSpace="nowrap">{index + 1}</Td>
                        <Td display="block" flexShrink="0" w="7" p="0" ml={[0, 0, 4]} gridArea="del">
                          {index !== 0 && <DeleteButton onClick={() => onDelete(index)} minW="auto" />}
                        </Td>
                        <Td display="block" p="0" ml={[1, 1, 4]} gridArea="ind" position="relative">
                          {index === 0 && (
                            <chakra.span
                              display={['block', 'block', 'none']}
                              position="absolute"
                              left="50%"
                              top="0"
                              transform="translate(-50%, -100%)"
                              fontSize="13px"
                            >
                              分人
                              <DividualExamples h="4" ml="2" mb="1" />
                            </chakra.span>
                          )}
                          <Input
                            value={name}
                            onChange={event => onNameChange(index, event)}
                            h="auto"
                            px="1.125rem"
                            py="0.875rem"
                            bgColor="white" />
                        </Td>
                        <Td display="block" flexShrink="0" w="4.5rem" p="0" ml={[1, 1, 2]} gridArea="ratio" position="relative">
                          {index === 0 && (
                            <chakra.span
                              display={['block', 'block', 'none']}
                              position="absolute"
                              left="50%"
                              top="0"
                              transform="translate(-50%, -100%)"
                              fontSize="13px"
                            >比率</chakra.span>
                          )}
                          <Select
                            display="inline"
                            onChange={event => onRatioChange(index, event)}
                            value={ratio}
                            h="auto"
                            bgColor="white"
                            sx={{
                              py: "0.875rem"
                            }}
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </Select>
                        </Td>
                        <Td display="block" flexShrink="0" w="2.75rem" p="0" ml={[2, 2, 2.5]} gridArea="perc" textAlign="end">{percentages[index].toFixed(0)}%</Td>
                        <Td display={['flex', 'flex', 'block']} flexShrink="0" w="6" h={['full', 'full', 'auto']} p="0" ml={[0, 0, 6]} position={['absolute', 'absolute', 'relative']} top="0" right="0" justifyContent="center" alignItems="center" gridArea="handle">
                          <Handle tooltipLabel="ドラッグ＆ドロップで並び替え" w="full" color="gray.600" />
                        </Td>
                      </Tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Tbody>
            </Table>
          )}
        </Droppable>
      </DragDropContext>
      <AddButton label="他の分人を追加" onClick={addDividualInput} disabled={estate.dividuals.length === 10} mx="auto" mt={[8, 8, `${30 / 16}rem`]} maxW="300px" />
      <DuplicateRatiosInput
        estates={estates.filter(e => (e.id !== estate.id) && (e.dividuals.length > 1 || !(e.dividuals[0].name === '' && e.dividuals[0].ratio === 1 && e.dividuals[0].comfort === 1)))}
        current={estate}
        setEstate={setEstate}
        mt={[8, 8, `${30 / 16}rem`]} />
    </Box>
  );
}

type DuplicateRatiosInputProps = BoxProps & {
  estates: Estate[],
  current: Estate,
  setEstate(estate: Estate): void
};

function DuplicateRatiosInput({
  estates,
  current: {dividuals, ...estateProps},
  setEstate,
  ...boxProps
}: DuplicateRatiosInputProps) {
  const [selected, setSelected] = useState<number | undefined>(estates[0]?.id);
  const onClick = () => {
    const selectedEstate = estates.find(estate => estate.id === selected);
    if (selectedEstate === undefined) {
      return;
    }
    setEstate({
      ...estateProps,
      dividuals: [
        ...dividuals.filter(({name, ratio, comfort}, index) => !(index === 0 && name === '' && ratio === 1 && comfort === 1)),
        ...selectedEstate.dividuals.map(duplicateDividual)
      ]
    })
  };

  if (estates.length === 0) {
    return null;
  }

  return (
    <VStack spacing="4" {...boxProps}>
      <Select value={selected} onChange={event => setSelected(event.target.value ? Number.parseInt(event.target.value) : undefined)} bgColor="white">
        <option>複製元を選択</option>
        {estates.map(estate => 
          <option key={estate.id} value={estate.id}>“{estate.name}”のときの分人をコピー</option>)}
      </Select>
      <Button
        onClick={onClick}
        lineHeight="1.5"
        px="8"
        border="solid 2px"
        borderColor="blue.500"
        borderRadius="1.25em"
        bgColor="white"
        color="blue.500"
      >内容を反映させる</Button>
    </VStack>
  );
}

function DividualExamples(props: ButtonProps) {
  const {isOpen, onOpen, onClose} = useDisclosure({defaultIsOpen: true});

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="top"
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <Button
          type="button"
          display="inline-flex"
          minW="0"
          minH="0"
          p="1"
          fontSize="0.8rem"
          fontWeight="bold"
          lineHeight="none"
          borderRadius="50%"
          border="solid 2px"
          style={{
            aspectRatio: '1'
          }}
          {...props}
        >?</Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader>分人記入の例</PopoverHeader>
        <PopoverBody textAlign="start">家族、人名（山田太郎）、趣味（読書）、場所（お気に入りのバー）など</PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
